/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'

import { error, log } from '@/services/Log'
import {
  FEATURE_TOGGLES,
  ENABLE_CITIZENS,
  SHOP_ORIGIN,
} from '@/services/Configuration'

import { useClientSideCartAndUser } from '@/services/getSiteWideInitialProps'
import { useIsCitizensPayScriptLoaded } from '@/hooks/useIsCitizensPayScriptLoaded'
import { useCitizensPayCookieToggle } from '@/services/FramedCheckout/thirdPartyServices'

import type { SiteWideSSRProps } from '@/services/getSiteWideInitialProps'
import type { NextPage } from 'next'
import type { Product } from '@/types'
import type { BrandInfo } from '@/types/brand'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import useMobileDetect from '@/hooks/useMobileDetect'

import Row from '@csc/dls/Row'
import Col from '@csc/dls/Col'
import { productToOptionMapFetcher, ProductOptionsToSkuMap } from '@/helpers/productOptionsForSkuMapper'
import Head from 'next/head'

const ProductOptions = dynamic(import('@/components/Pdp/ProductOptions'))
const Default = dynamic(import('@/layouts/default'))
const PdpTrackingServices = dynamic(import('@/hooks/PdpTrackingServices'))
const ProductContextProvider = dynamic(import('@/hooks/useProduct/index'))
const CitizensPayPdpWidget = dynamic(import('@/components/Pdp/CitizensPayPdpWidget/CitizensPayPdpWidget'))

const ltkPdpRecEnabled = !!FEATURE_TOGGLES?.ltkPdpRecEnabled
const collectionPdpEnabled = !!FEATURE_TOGGLES?.collectionPdpEnabled

const ListrakRec = ltkPdpRecEnabled
  ? dynamic(import('@/components/Pdp/ListrakRec'))
  : ((() => null) as React.FC)

const CollectionRecommendationWidget = collectionPdpEnabled
  ? dynamic(import('@/components/Pdp/CollectionRecommendationWidget/index'))
  : ((() => null) as React.FC)

const ProductDetails = dynamic(import('@/components/Pdp/ProductDetails'))
const ProductImages = dynamic(import('@/components/Pdp/ProductImages'))
const AddProductToCartForm = dynamic(import('@/components/Pdp/AddProductToCartForm'))
const Breadcrumbs = dynamic(import('@/components/Pdp/Breadcrumbs'))
const AccordionProductDetails = dynamic(import('@/components/Pdp/AccordionProductDetails'))
const BrandDescription = dynamic(import('@/components/Pdp/BrandDescription'))
const ProductSchema = dynamic(import('@/components/Pdp/ProductSchema/index'))

interface ProductPageProps extends SiteWideSSRProps {
  product: Product,
  defaultVariantSku: string,
  brand_Info: BrandInfo | null,
  productOptionsToSkuMap: ProductOptionsToSkuMap,
  imagesToPreload: string[],
}

const ProductPage: NextPage<ProductPageProps> = ({
  product,
  sitewideBanner,
  megaNav,
  defaultVariantSku,
  brand_Info,
  productOptionsToSkuMap,
  imagesToPreload,
}) => {
  const { cart, setCart, user } = useClientSideCartAndUser()
  const citizensPayScriptLoaded = useIsCitizensPayScriptLoaded()
  const [shouldShowCitizensPay, setShouldShowCitizensPay] = useState(ENABLE_CITIZENS)
  const isMobile = useMobileDetect()
  useEffect(() => {
    const getCitizensDisplayState = () => {
      let citizensPayCookieEnabled = false
      try {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        citizensPayCookieEnabled = useCitizensPayCookieToggle()
      } catch (e) {
        error('Failed to check if citizensPayCookie is enabled')
      }
      setShouldShowCitizensPay(ENABLE_CITIZENS || citizensPayCookieEnabled)
    }
    getCitizensDisplayState()
  }, [])

  // Computed values from state and props
  const metaTags = {
    title: String(product?.page_title),
    description: String(product?.meta_description),
  }
  const brand_description = String(brand_Info?.category?.meta_description || '')
  const brand_description_title = 'About The Brand'
  const showCitizensPayWidget = shouldShowCitizensPay && citizensPayScriptLoaded
  return (
    <>
      <Head>
        {
          imagesToPreload.map((imgUrl, index) => (
            <link key={imgUrl} rel="preload" as="image" href={imgUrl} id={`preloaded-image-${index}`} />
          ))
        }
      </Head>
      <Default
        user={user}
        sitewideBanner={sitewideBanner}
        megaNav={megaNav}
        meta={metaTags}
        cart={cart}
        onUpdateCart={setCart}
        pageType="product"
      >
        <ProductContextProvider
          cart={cart}
          setCart={setCart}
          product={product}
          defaultVariantSku={defaultVariantSku}
          user={user}
        >
          <PdpTrackingServices
            cart={cart}
            user={user}
          >
            <Row cols={1} data-currency-code="USD" className="mt-8">
              <Breadcrumbs />
              <Row gapX="lg">
                <Col span={12} className="md:hidden">
                  <ProductDetails />
                </Col>
                {showCitizensPayWidget && isMobile && (
                <Col span={12}>
                  <CitizensPayPdpWidget />
                </Col>
                )}
                <Col span={{ sm: 12, lg: 7 }} className="w-full">
                  <ProductImages />
                </Col>
                <Col span={{ sm: 12, lg: 5 }}>
                  <Row gap="xl" cols={1}>
                    <div className="hidden md:block">
                      <ProductDetails />
                    </div>
                    {showCitizensPayWidget && !isMobile && (
                    <CitizensPayPdpWidget />
                    )}
                    <ProductOptions productOptionsToSkuMap={productOptionsToSkuMap} />
                    <AddProductToCartForm setCart={setCart} />
                    <AccordionProductDetails />
                  </Row>
                  {brand_description && (
                  <BrandDescription
                    brand_description_title={brand_description_title}
                    brand_description={String(brand_description)}
                  />
                  )}
                </Col>
                {ltkPdpRecEnabled ? (
                  <Col span={{ sm: 12, lg: 12 }} className="w-full">
                    <ListrakRec />
                  </Col>
                ) : null}
              </Row>
            </Row>
            {collectionPdpEnabled && <CollectionRecommendationWidget title="Related Items" />}
          </PdpTrackingServices>
          <ProductSchema />
        </ProductContextProvider>
      </Default>
    </>
  )
}

ProductPage.getInitialProps = async (ctx) => {
  try {
    const { query } = ctx
    const sku = String(query.primarySku)
    const queryWithoutSlug = { ...query }
    delete queryWithoutSlug.slug
    delete queryWithoutSlug.id

    const [
      { getSiteWideInitialProps },
      { Product },
      { Brand },
    ] = await allPromisesWithRetries(() => [
      import('@/services/getSiteWideInitialProps'),
      import('@/services/Product'),
      import('@/services/brand'),
    ])
    log('Product page getInitialProps', { sku })
    type ProductBrandFetcher = (product: Product) => Promise<[Product, BrandInfo | null]>
    const fetchProductBrand: ProductBrandFetcher = async (product) => {
      if (product?.brand_id) {
        return [product, await Brand.getBrandById(product.brand_id)]
      }
      return [product, null]
    }

    const [[product, brand_Info], siteWideProps] = await Promise.all([
      Product
        .bySku({ sku })
        .then(fetchProductBrand),
      getSiteWideInitialProps(ctx),
    ])

    if (!product) {
      throw new Error(`Product not found for sku: ${sku}`)
    }
    const defaultVariantSku = typeof query.sku === 'string' ? query.sku : product.variants[0].sku
    const productOptionsToSkuMap = productToOptionMapFetcher({
      product,
      targetSku: defaultVariantSku,
    })
    log(`productOptionsMap for ${defaultVariantSku}`, {
      defaultVariantSku,
      productOptionsToSkuMap,
    })

    const imagesToPreload = (
      product?.variants.find((v) => v.sku === defaultVariantSku)?.images
      || []
    )
      .map((image) => image.url_square)
      .slice(0, 6)

    return {
      ...siteWideProps,
      imagesToPreload,
      productOptionsToSkuMap,
      defaultVariantSku,
      product,
      brand_Info,
    }
  } catch (err) {
    error('PDP SSR Failed', err)
    const params = new URLSearchParams({ pdpslug: `${String(ctx.asPath || '') || ''}`.replace(/\//g, '') })
    const location = `${SHOP_ORIGIN}/PageNotFound?${String(params)}`
    if (process.browser && typeof window !== 'undefined' && window.location.href) {
      window.location.href = location
    } else if (ctx.res) {
      // Tell 302 proxy not to cache response
      // https://docs.layer0.co/guides/caching#section_caching_private_responses
      ctx.res?.setHeader?.('cache-control', 'private')
      ctx.res?.writeHead?.(302, { location })
      ctx.res?.end?.()
    }
    // At this point the page should have reloaded on the 404
    // We no longer return props for a failed scenario
    // This allow us to do strong types on the component props
    // While the page is loading, code execution will
    // continue, so we'll await a never-resolving
    // promise to make sure our page never
    // gets rendered.
    await new Promise((resolve) => setTimeout(resolve, 15000)) // Max 15s to redirect
    throw err
  }
}

export default ProductPage
